/**
 * Delivery Schedule Settings
 * Global preferences for digest delivery
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../config/msalConfig';
import {
    Form,
    Switch,
    Select,
    InputNumber,
    Button,
    Space,
    Typography,
    Divider,
    Alert,
    message,
    Card
} from 'antd';
import {
    SaveOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { updatePreferences } from '../../services/digestService';

const { Title, Text } = Typography;
const { Option } = Select;

export default function DeliveryScheduleSettings({ preferences, onUpdate }) {
    const { instance, accounts } = useMsal();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [globalEnabled, setGlobalEnabled] = useState(true);

    useEffect(() => {
        if (preferences) {
            form.setFieldsValue({
                globalEnabled: preferences.GlobalEnabled,
                defaultFrequency: preferences.DefaultFrequency,
                defaultDeliveryHour: preferences.DefaultDeliveryHour,
                timezone: preferences.Timezone,
                locale: preferences.Locale,
                includeTrends: preferences.IncludeTrends,
                includeRecommendations: preferences.IncludeRecommendations,
                includeCharts: preferences.IncludeCharts
            });
            setGlobalEnabled(preferences.GlobalEnabled);
        }
    }, [preferences, form]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            await updatePreferences(values, response.accessToken);

            message.success('Preferences saved successfully');
            onUpdate(values);
        } catch (error) {
            console.error('Error saving preferences:', error);
            message.error('Failed to save preferences');
        } finally {
            setLoading(false);
        }
    };

    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
        { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
        { value: 'UTC', label: 'UTC' },
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
        { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
        { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
        { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Header */}
            <div>
                <Title level={4}>Global Preferences</Title>
                <Text type="secondary">
                    Configure default settings for all module digest subscriptions
                </Text>
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Main Form */}
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    globalEnabled: true,
                    defaultFrequency: 'weekly',
                    defaultDeliveryHour: 8,
                    timezone: 'America/Los_Angeles',
                    locale: 'en-US',
                    includeTrends: true,
                    includeRecommendations: true,
                    includeCharts: false
                }}
            >
                {/* Master Enable/Disable */}
                <Card style={{ marginBottom: '24px', background: globalEnabled ? '#f6ffed' : '#fff1f0' }}>
                    <Form.Item
                        name="globalEnabled"
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Space>
                                {globalEnabled ? (
                                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                                ) : (
                                    <CloseCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                                )}
                                <div>
                                    <Title level={5} style={{ margin: 0 }}>
                                        {globalEnabled ? 'Digest Emails Enabled' : 'Digest Emails Disabled'}
                                    </Title>
                                    <Text type="secondary">
                                        {globalEnabled
                                            ? 'You will receive digest emails based on your subscriptions'
                                            : 'All digest emails are paused'
                                        }
                                    </Text>
                                </div>
                            </Space>
                            <Switch
                                checked={globalEnabled}
                                onChange={setGlobalEnabled}
                                size="large"
                            />
                        </div>
                    </Form.Item>
                </Card>

                {/* Schedule Settings */}
                <Title level={5}>Default Schedule</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    These settings will be used as defaults for new subscriptions
                </Text>

                <Form.Item
                    label="Default Frequency"
                    name="defaultFrequency"
                    rules={[{ required: true, message: 'Please select default frequency' }]}
                >
                    <Select disabled={!globalEnabled}>
                        <Option value="daily">
                            Daily - Receive digest every day
                        </Option>
                        <Option value="weekly">
                            Weekly - Receive digest every Monday
                        </Option>
                        <Option value="monthly">
                            Monthly - Receive digest on 1st of month
                        </Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Default Delivery Hour"
                    name="defaultDeliveryHour"
                    rules={[
                        { required: true, message: 'Please select delivery hour' },
                        { type: 'number', min: 0, max: 23, message: 'Hour must be 0-23' }
                    ]}
                    extra="Hour in your timezone when digests should be sent"
                >
                    <InputNumber
                        min={0}
                        max={23}
                        style={{ width: '200px' }}
                        formatter={value => `${value}:00`}
                        disabled={!globalEnabled}
                    />
                </Form.Item>

                <Form.Item
                    label="Timezone"
                    name="timezone"
                    rules={[{ required: true, message: 'Please select timezone' }]}
                >
                    <Select
                        showSearch
                        disabled={!globalEnabled}
                        placeholder="Select your timezone"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {timezones.map(tz => (
                            <Option key={tz.value} value={tz.value}>
                                {tz.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Divider />

                {/* Content Preferences */}
                <Title level={5}>Content Preferences</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    Choose what information to include in digest emails
                </Text>

                <Form.Item
                    name="includeTrends"
                    valuePropName="checked"
                    style={{ marginBottom: '12px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch disabled={!globalEnabled} />
                        <div style={{ marginLeft: '12px' }}>
                            <Text strong>Include Trends Analysis</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Show trends and patterns identified in the data
                            </Text>
                        </div>
                    </div>
                </Form.Item>

                <Form.Item
                    name="includeRecommendations"
                    valuePropName="checked"
                    style={{ marginBottom: '12px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch disabled={!globalEnabled} />
                        <div style={{ marginLeft: '12px' }}>
                            <Text strong>Include Recommendations</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Show actionable recommendations based on the data
                            </Text>
                        </div>
                    </div>
                </Form.Item>

                <Form.Item
                    name="includeCharts"
                    valuePropName="checked"
                    style={{ marginBottom: '12px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch disabled={!globalEnabled} />
                        <div style={{ marginLeft: '12px' }}>
                            <Text strong>Include Charts (Coming Soon)</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Embed visual charts and graphs in the email
                            </Text>
                        </div>
                    </div>
                </Form.Item>

                <Form.Item
                    label="Language"
                    name="locale"
                    rules={[{ required: true, message: 'Please select language' }]}
                >
                    <Select disabled={!globalEnabled}>
                        <Option value="en-US">English (US)</Option>
                        <Option value="en-GB">English (UK)</Option>
                        <Option value="es-ES">Spanish</Option>
                        <Option value="fr-FR">French</Option>
                        <Option value="de-DE">German</Option>
                    </Select>
                </Form.Item>

                <Divider />

                {/* Save Button */}
                <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={loading}
                        size="large"
                    >
                        Save Preferences
                    </Button>
                </Form.Item>
            </Form>

            {/* Help Text */}
            <Alert
                message="Note"
                description="Changes to these preferences will apply to all your current and future module subscriptions. Individual subscriptions can override these defaults."
                type="info"
                showIcon
            />
        </Space>
    );
}
